/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/cidModifierApi.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    API functions for CID Modifiers.
    Centralizes all API calls related to CID Modifiers.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  CidModifier,
  CidModifierPayload,
  ListCidModifiersResponse,
  CidModifierResponse,
} from '../types';

export const CID_MODIFIERS_ENDPOINT = '/api/telephony/cid-modifiers';

/**
 * Generic API request helper
 */
async function requestCidModifiersApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit,
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List CID Modifiers with pagination and search
 */
export async function fetchCidModifiers(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string,
): Promise<ListCidModifiersResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${CID_MODIFIERS_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch CID modifiers');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as CidModifier[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0,
    },
  };
}

/**
 * Get single CID Modifier by ID
 */
export async function fetchCidModifier(id: string | number): Promise<CidModifier> {
  const { data } = await requestCidModifiersApi<CidModifier>(
    `${CID_MODIFIERS_ENDPOINT}/${id}`,
    'Failed to fetch CID modifier',
  );
  return data;
}

/**
 * Create new CID Modifier
 */
export async function createCidModifier(
  payload: CidModifierPayload,
): Promise<CidModifier> {
  const { data } = await requestCidModifiersApi<CidModifier>(
    CID_MODIFIERS_ENDPOINT,
    'Failed to create CID modifier',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Update CID Modifier
 */
export async function updateCidModifier(
  id: string | number,
  payload: CidModifierPayload,
): Promise<CidModifier> {
  const { data } = await requestCidModifiersApi<CidModifier>(
    `${CID_MODIFIERS_ENDPOINT}/${id}`,
    'Failed to update CID modifier',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Delete CID Modifier
 */
export async function deleteCidModifier(id: string | number): Promise<void> {
  await requestCidModifiersApi<null>(
    `${CID_MODIFIERS_ENDPOINT}/${id}`,
    'Failed to delete CID modifier',
    {
      method: 'DELETE',
    },
  );
}

